import { Timestamps, Versioned } from './shared.types';

/**
 * One task list for the whole system (docs/plans/orders-and-transfers.md
 * §4.13): what is waiting on someone in a store. Each type is described in
 * `TASK_TYPES`; add values, never rename or reuse them.
 */
export enum TaskType {
  /** An order waits to be planned (Orders & Transfers, PR 6). */
  ORDER_PLAN = 'ORDER_PLAN',
  /** A planned transfer waits to be dispatched by its source. */
  TRANSFER_DISPATCH = 'TRANSFER_DISPATCH',
  /** A dispatched transfer waits to be received by its destination. */
  TRANSFER_RECEIVE = 'TRANSFER_RECEIVE',
  /**
   * A stock change of a product could not be recorded: check its stock.
   * Done when the product is counted in the store.
   */
  INVENTORY_CHECK = 'INVENTORY_CHECK',
  /**
   * Created by a person for someone else (PR 5c): its title is `title`, it
   * is assigned to a person or a role, and the assignee ticks it.
   */
  MANUAL = 'MANUAL',
  /**
   * An order nears its expiry with demand or transfers still open (PR 6b):
   * for its requester. Done when it is planned and dispatched, cancelled
   * or expired.
   */
  ORDER_EXPIRING = 'ORDER_EXPIRING',
  /**
   * Sales took stock a reservation holds (reserved > on hand): for the
   * owner, to count or release. Done when the stock covers it again.
   */
  STOCK_OVERSOLD = 'STOCK_OVERSOLD',
  /** Goods sent back to a source wait for its return check (PR 7). */
  TRANSFER_RETURN = 'TRANSFER_RETURN',
  /** A received transfer has differences to decide (PR 7). */
  TRANSFER_SETTLE = 'TRANSFER_SETTLE',
  /** Received goods wait in pending for their price (PR 7). */
  PRICE_APPROVAL = 'PRICE_APPROVAL',
}

/** OPEN creates or reopens a task; DONE and WITHDRAWN close it. */
export enum TaskEventOp {
  OPEN = 'OPEN',
  DONE = 'DONE',
  WITHDRAWN = 'WITHDRAWN',
}

export enum TaskStatus {
  OPEN = 'OPEN',
  DONE = 'DONE',
  /** Its reason disappeared (the order was cancelled). */
  WITHDRAWN = 'WITHDRAWN',
}

/**
 * SYSTEM tasks are opened and closed by the service that owns the work;
 * MANUAL ones are created by a person and ticked by the assignee.
 */
export enum TaskOrigin {
  SYSTEM = 'SYSTEM',
  MANUAL = 'MANUAL',
}

/** Urgent tasks sort first with a red mark (and send WhatsApp when on). */
export enum TaskPriority {
  NORMAL = 'NORMAL',
  URGENT = 'URGENT',
}

/** MINE: the user's tasks; TEAM: every task of the store (`tasks:view_all`). */
export enum TaskScope {
  MINE = 'MINE',
  TEAM = 'TEAM',
}

/** Values filling a task type's title (e.g. `{ reference: 'T-0042' }`). */
export type TaskParams = Record<string, string | number>;

/**
 * Published by a producing service with `tryPublishTaskEvent` (backend-shared)
 * to the notifications topic (`category=TASK`). Idempotent by `source_key`:
 * an event older than the task's last `occurred_at` is ignored.
 */
export interface TaskEvent {
  op: TaskEventOp;
  type: TaskType;
  /** Stable per piece of work, e.g. `transfer:<id>:receive`. */
  source_key: string;
  merchant_id: string;
  store_id: string;
  params?: TaskParams;
  /**
   * Narrows the task to this one user (null: anyone holding the action).
   * Required on every event, closes included, so a close delivered before
   * its OPEN still knows who the task is for.
   */
  assignee_id: string | null;
  /** When the work entered (OPEN) or left (DONE, WITHDRAWN) its state. */
  occurred_at: string;
  /**
   * OPEN only: when the work entered the state it is waiting in (e.g. the
   * transfer's `actionable_since`), so an OPEN delivered out of order still
   * sets the right waiting time. Missing = `occurred_at` when the task
   * (re)opens, unchanged while it stays open.
   */
  waiting_since?: string;
  /** The user whose action caused the event. */
  actor?: TaskActor;
  /**
   * DONE or WITHDRAWN only: ignored unless the task is open, so a producer
   * may close tasks it never opened (every count closes the product's stock
   * check) without creating or re-closing one. Optional; missing = false.
   */
  only_if_open?: boolean;
}

export interface TaskActor {
  id: string;
  name: string;
}

/** A `TASK` item: PK `MERCHANT#<merchant_id>`, SK `TASK#<source_key>`. */
export interface Task extends Timestamps, Versioned {
  /** The `source_key`. */
  id: string;
  merchant_id: string;
  store_id: string;
  type: TaskType;
  origin: TaskOrigin;
  status: TaskStatus;
  /** Who sees it: holders of this action in the store (`TASK_TYPES`). */
  required_action: string;
  /** The app where the work is done (`TASK_TYPES`). */
  app: string;
  params?: TaskParams;
  assignee_id?: string;
  /** Manual tasks: the assignee's name, for the creator and the report. */
  assignee_name?: string;
  /**
   * Manual tasks: assigned to everyone holding this role in the store (the
   * first to tick it closes it for all). Never set with `assignee_id`.
   */
  assignee_role_id?: string;
  assignee_role_name?: string;
  /** Manual tasks: the short written title (required for MANUAL). */
  title?: string;
  description?: string;
  /** A VERIFIED AUDIO attachment (`entity_type: TASK`), at most 2 minutes. */
  audio_attachment_id?: string;
  /** VERIFIED photos or PDFs (`entity_type: TASK`). */
  attachment_ids?: string[];
  /** Overdue after it. */
  due_at?: string;
  /** Optional for backwards compatibility; missing = NORMAL. */
  priority?: TaskPriority;
  /** The note left by whoever ticked a manual task. */
  done_note?: string;
  /** When it last became OPEN. */
  waiting_since?: string;
  closed_at?: string;
  closed_by_id?: string;
  closed_by_name?: string;
  /** The newest event applied; older ones are ignored. */
  last_occurred_at: string;
  created_by_id: string;
  created_by_name: string;
}

/** `GET /notifications/tasks/{merchant_id}/stores/{store_id}` */
export interface GetTasks {
  merchant_id: string;
  store_id: string;
  /** The calling app: its tasks come first. */
  app?: string;
  /** Missing = MINE. TEAM needs `tasks:view_all` in the store. */
  scope?: TaskScope;
}

/** The signed-in user's tasks in one store. */
export interface TaskList {
  /** Open, this app's first, then oldest waiting first. */
  open: Task[];
  /** Closed in the last 24 hours, newest first. */
  recently_closed: Task[];
  /** More open tasks exist than the list holds. */
  has_more: boolean;
}

/** `GET /notifications/tasks/{merchant_id}/summary` */
export interface GetTaskSummary {
  merchant_id: string;
}

export interface StoreTaskSummary {
  store_id: string;
  /** Open tasks the user sees in this store. */
  open: number;
  /** A task the user sees closed here in the last 24 hours. */
  recently_closed: boolean;
}

/** Per store of the signed-in user: the badge and the "other stores" line. */
export interface TaskSummary {
  stores: StoreTaskSummary[];
  /**
   * Open manual tasks the user created (at most 100), for *Created by me*.
   * Optional for backwards compatibility; missing = 0.
   */
  created_open?: number;
}

/**
 * `POST /notifications/tasks/{merchant_id}` (`tasks:create`, Idempotency-Key):
 * a manual task for one person or one role of the store.
 */
export interface CreateTask {
  /**
   * A ULID made by the client before uploading the files, so they can be
   * attached to it (`entity_id`). The task's id is `manual:<id>`.
   */
  id: string;
  store_id: string;
  title: string;
  description?: string;
  audio_attachment_id?: string;
  attachment_ids?: string[];
  due_at?: string;
  /** Missing = NORMAL. */
  priority?: TaskPriority;
  /** Exactly one of `assignee_id` and `assignee_role_id`. */
  assignee_id?: string;
  assignee_role_id?: string;
}

/** `POST /notifications/tasks/{merchant_id}/{task_id}/done` (assignee or role member). */
export interface CompleteTask {
  note?: string;
}

/** `GET /notifications/tasks/{merchant_id}/created?cursor=`: the user's manual tasks. */
export interface GetCreatedTasks {
  merchant_id: string;
  cursor?: string;
}

/** Open ones first, newest first, then closed ones, last closed first. */
export interface TaskPage {
  tasks: Task[];
  cursor?: string;
}

/**
 * `GET /notifications/tasks/{merchant_id}/report` (`tasks:view_all`): the
 * store's tasks active in [`from`, `to`]: created before `to` and open, or
 * closed at or after `from`.
 */
export interface GetTaskReport {
  merchant_id: string;
  store_id: string;
  from: string;
  to: string;
  cursor?: string;
  assignee_id?: string;
  assignee_role_id?: string;
  created_by_id?: string;
  origin?: TaskOrigin;
  type?: TaskType;
  priority?: TaskPriority;
  status?: TaskStatus;
}

export interface TaskReportTotals {
  /** Created in the range. */
  created: number;
  /** Done in the range. */
  done: number;
  /** Open now. */
  open: number;
  /** Past their due date: still open, or closed after it. */
  overdue: number;
}

/** Pages of 50, newest created first; totals cover the whole selection. */
export interface TaskReport {
  tasks: Task[];
  totals: TaskReportTotals;
  cursor?: string;
  /** The selection was cut at its maximum: narrow the range. */
  truncated: boolean;
}
